import { Injectable, inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { UntilDestroy } from '@ngneat/until-destroy';
import { AuthFacade } from 'mbs-ui-shared@facades/auth.facade';
import Brand, { BrandingCompany } from 'mbs-ui-shared@models/Brand';
import BrandImage from 'mbs-ui-shared@models/BrandImage';
import BrandLicense from 'mbs-ui-shared@models/BrandLicense';
import BrandVersion from 'mbs-ui-shared@models/BrandVersion';
import { BrandsService } from 'mbs-ui-shared@services/brands.service';
import { BrandsImagesService } from 'mbs-ui-shared@services/brandsImages.service';
import { BehaviorSubject, EMPTY, Observable, finalize, map, startWith, switchMap, tap } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { BrandsStore } from '../store/brands/brands.store';

@UntilDestroy()
@Injectable({
  providedIn: 'root'
})
export class BrandsFacade {
  private service = inject(BrandsService);
  private brandsStore = inject(BrandsStore);
  private auth = inject(AuthFacade);
  private imagesService = inject(BrandsImagesService);

  private myLicenses$ = new BehaviorSubject<BrandLicense[]>(null);
  public licenses$ = this.myLicenses$.asObservable();

  public loading$ = toObservable(this.brandsStore.loading);
  public entities$ = toObservable(this.brandsStore.entities);

  public imagesRequestedId = '';
  public imagesIsLoading = false;
  public imagesData$ = new BehaviorSubject<{ images: BrandImage[]; brandId: string }>({ images: [], brandId: '' });

  public getAll$(): Observable<Brand[]> {
    this.brandsStore.setLoading(true);
    return this.service.getAll().pipe(
      tap((data) => this.brandsStore.setAll(data)),
      catchError(() => {
        this.brandsStore.setLoading(false);
        return EMPTY;
      }),
      finalize(() => this.brandsStore.setLoading(false))
    );
  }

  public getById$(id: string): Observable<Brand> {
    return this.service.getByKey(id).pipe(
      tap((brand) => this.brandsStore.upsertOne(brand)),
      startWith({ id } as Brand)
    );
  }

  public add$(brand: Brand): Observable<Brand> {
    return this.service.add(brand).pipe(
      tap((created) => {
        if (created?.id) this.brandsStore.upsertOne(created);
      }),
      switchMap((created) =>
        this.service.getAll().pipe(
          tap((all) => this.brandsStore.setAll(all)),
          map(() => created)
        )
      )
    );
  }

  public update$(brand: Brand): Observable<Brand> {
    return this.service.update(brand).pipe(
      tap((updated) => {
        if (updated?.id) this.brandsStore.upsertOne(updated);
      }),
      switchMap(() =>
        this.service.getAll().pipe(
          tap((all) => this.brandsStore.setAll(all)),
          map(() => brand)
        )
      )
    );
  }

  public delete$(id: string): Observable<boolean> {
    return this.service.delete(id).pipe(
      tap(() => this.brandsStore.removeOne(id)),
      switchMap(() => this.service.getAll()),
      tap((all) => this.brandsStore.setAll(all)),
      map(() => true)
    );
  }

  public getVersions$(brandId: string): Observable<BrandVersion[]> {
    return this.service.getVersions$(brandId);
  }

  public getDownloadLink$(brandId: string, code: string): Observable<string> {
    return this.service.getDownloadLink$(brandId, code);
  }

  public getDefault$(): Observable<Brand> {
    return this.service.getDefault$();
  }

  public getActive$(): Observable<Brand[]> {
    return this.service.getActive$();
  }

  public getAllBrandingCompanies$(): Observable<BrandingCompany[]> {
    return this.service.getAllBrandingCompanies$();
  }

  public checkSymbols$(value: string): Observable<any> {
    return this.service.checkSymbols$(value);
  }

  public deassignLicenseFromBrand$(licenseId: string): Observable<void> {
    return this.service.assignLicenseToBrand$(licenseId, null);
  }

  // licenses

  public getLicenses(): void {
    this.service.getLicenses().subscribe((licenses) => this.myLicenses$.next(licenses));
  }

  public getLicenses$(): Observable<BrandLicense[]> {
    return this.service.getLicenses();
  }

  public assignLicenseToBrand$(licenseId: string, brandId: string): Observable<void> {
    return this.service
      .assignLicenseToBrand$(licenseId, brandId)
      .pipe(switchMap((res) => this.auth.fetchCurrentUser$.pipe(map(() => res))));
  }

  // images

  imagesGet$(brandId: string): Observable<BrandImage[]> {
    this.imagesRequestedId = brandId;
    this.imagesIsLoading = true;
    return this.imagesService.get(brandId).pipe(
      tap((data) => this.imagesData$.next({ images: data, brandId })),
      finalize(() => {
        this.imagesIsLoading = false;
      })
    );
  }

  imagesUpdate$(brandId: string, data: BrandImage[]): Observable<any> {
    this.imagesRequestedId = brandId;
    return this.imagesService.update(brandId, data);
  }

  imagesAdd$(brandId: string, formData: FormData): Observable<any> {
    return this.imagesService.add(brandId, formData);
  }

  imagesClear(): void {
    this.imagesData$.next({ brandId: '', images: [] });
  }
}
